import { request, type RequestOptions } from '@/common/services/request';
import { createIdempotencyKey } from '@/common/utils/idempotency';
import type {
  CreateOrderRequest,
  CursorPage,
  Order,
  OrderPreview,
  OrderSubmissionResponse,
  OrderSubmissionTicket,
} from '@/types/api';

export type {
  CreateOrderRequest,
  CursorPage,
  Order,
  OrderPreview,
  OrderSubmissionResponse,
  OrderSubmissionTicket,
  OrderStatus,
  OrderDirection,
  OrderMode,
} from '@/types/api';

type OrderRequestExtras = Pick<
  RequestOptions,
  'expectedWallet' | 'authRetryLimit' | 'showError'
>;

/** Build Idempotency-Key + X-Client-Order-ID (reuse both on retry). */
export function createOrderIdempotencyHeaders(prefix = 'rolling'): {
  idempotencyKey: string;
  clientOrderId: string;
} {
  const key = createIdempotencyKey(prefix);
  return {
    idempotencyKey: key,
    clientOrderId: key,
  };
}

/** POST /v1/orders/preview — no debit */
export const previewOrder = (
  data: CreateOrderRequest,
  extras: OrderRequestExtras & { purpose?: 'prefetch' } = {}
): Promise<OrderPreview> => {
  return request({
    url: '/v1/orders/preview',
    method: 'POST',
    data,
    header: extras.purpose ? { 'X-Order-Preview-Purpose': extras.purpose } : undefined,
    expectedWallet: extras.expectedWallet,
    showError: extras.showError ?? false,
  });
};

export interface CreateOrderOptions extends OrderRequestExtras {
  /** Reuse on retry; if omitted, a new key pair is generated. */
  idempotencyKey?: string;
  clientOrderId?: string;
  keyPrefix?: string;
  /** direct-v1 受理凭证 */
  orderSubmissionMode?: string;
  orderSubmissionToken?: string;
}

/** POST /v1/orders — Idempotency-Key + X-Client-Order-ID + 可选 direct-v1 头 */
export const createOrder = (
  data: CreateOrderRequest,
  options: CreateOrderOptions = {}
): Promise<Order> => {
  const headers =
    options.idempotencyKey && options.clientOrderId
      ? {
          idempotencyKey: options.idempotencyKey,
          clientOrderId: options.clientOrderId,
        }
      : createOrderIdempotencyHeaders(options.keyPrefix ?? 'rolling');

  return request({
    url: '/v1/orders',
    method: 'POST',
    data,
    idempotencyKey: headers.idempotencyKey,
    clientOrderId: headers.clientOrderId,
    orderSubmissionMode: options.orderSubmissionMode,
    orderSubmissionToken: options.orderSubmissionToken,
    // §5.8：正式单 401 后不自动重放
    authRetryLimit: options.authRetryLimit ?? 0,
    expectedWallet: options.expectedWallet,
    showError: options.showError ?? false,
  });
};

export interface ListOrdersParams {
  orderMode?: string;
  limit?: number;
  cursor?: string;
  symbol?: string;
  /** ACTIVE | SETTLED | CURRENT | HISTORY | 具体库状态 */
  status?: string;
  /** 0 普通 / 1 AI。CURRENT/HISTORY 不再用它分类 */
  positionType?: 0 | 1 | string;
  /**
   * HISTORY 按下单来源：ORDINARY 普通页，AI 预测页。转普通保险后仍为 AI。
   * 普通历史配合 includeCopy=true 时，items 可含 COPY（跟单）单。
   */
  orderSource?: 'ORDINARY' | 'AI' | 'COPY' | string;
  /**
   * 仅普通事件合约历史使用：status=HISTORY&orderSource=ORDINARY&includeCopy=true
   * 时一并返回普通单与跟单单。AI 历史 / CURRENT 持仓勿传。
   */
  includeCopy?: boolean;
}

/** GET /v1/orders */
export const listOrders = (
  params: ListOrdersParams = {},
  extras: OrderRequestExtras = {}
): Promise<CursorPage<Order>> => {
  return request({
    url: '/v1/orders',
    method: 'GET',
    data: params,
    expectedWallet: extras.expectedWallet,
    showError: extras.showError ?? false,
  });
};

/** POST /v1/orders/submission — ticket | prepare | resolve */
export const submitOrderRequest = (
  data:
    | { action: 'ticket' }
    | {
        clientOrderId: string;
        idempotencyKey: string;
        action: 'prepare' | 'resolve';
      },
  extras: OrderRequestExtras = {}
): Promise<OrderSubmissionResponse | OrderSubmissionTicket> => {
  return request({
    url: '/v1/orders/submission',
    method: 'POST',
    data,
    timeout: 8_000,
    expectedWallet: extras.expectedWallet,
    authRetryLimit: extras.authRetryLimit ?? 1,
    showError: extras.showError ?? false,
  });
};

/** 提前领取受理凭证 */
export const requestSubmissionTicket = (
  extras: OrderRequestExtras = {}
): Promise<OrderSubmissionTicket> => {
  return submitOrderRequest({ action: 'ticket' }, extras) as Promise<OrderSubmissionTicket>;
};

/** GET /v1/orders/:id after submission status is ACCEPTED */
export const confirmOrderSubmission = async (
  orderId: string,
  extras: OrderRequestExtras = {}
): Promise<Order> => {
  return getOrder(orderId, extras);
};

/** GET /v1/orders/:id */
export const getOrder = (
  id: string,
  extras: OrderRequestExtras = {}
): Promise<Order> => {
  return request({
    url: `/v1/orders/${encodeURIComponent(id)}`,
    method: 'GET',
    expectedWallet: extras.expectedWallet,
    authRetryLimit: extras.authRetryLimit ?? 1,
    showError: extras.showError ?? false,
  });
};
